import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CarteraDashboard } from '../Cartera/CarteraDashboard';
import { CarteraLista } from '../Cartera/CarteraLista';
import { CarteraMatriz } from '../Cartera/CarteraMatriz';
import { CarteraDetalle } from '../Cartera/CarteraDetalle';
import { CarteraCobranzaMes } from '../Cartera/CarteraCobranzaMes';
import { CarteraEstadosCuenta } from '../Cartera/CarteraEstadosCuenta';
import { CarteraConversaciones } from '../Cartera/CarteraConversaciones';
import { CarteraAvisos } from '../Cartera/CarteraAvisos';
import { carteraApi } from '../Cartera/api';
import { DATOS_PAGO } from '../Cartera/mensajes';
import '../Cartera/cartera-dashboard.css';

const TABS = [
  { key: 'tablero', label: 'Tablero' },
  { key: 'clientes', label: 'Cartera de clientes' },
  { key: 'matriz', label: 'Matriz de cobranza' },
  { key: 'cobranza', label: 'Cobranza del mes' },
  { key: 'edos', label: 'Estados de cuenta' },
  { key: 'convers', label: 'Conversaciones' },
  { key: 'avisos', label: 'Avisos de pago' },
  // { key: 'gestion', label: 'Gestión' }, // Fase siguiente (necesita tabla `gestiones`).
];
const TAB_KEYS = TABS.map((t) => t.key);

/*
 * Navegación por URL (query params) → recargar / atrás / adelante conserva el
 * lugar exacto sin volver al inicio:
 *   ?tab=clientes                 → pestaña activa
 *   ?credito=123                  → detalle de un crédito (sobre cualquier tab)
 *   ?tab=edos&edo=45              → estado de cuenta con contrato preseleccionado
 *   ?tab=convers&conv=12          → conversación preseleccionada
 */
export const CarteraPage = () => {
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const controller = new AbortController();
    carteraApi.getConfigMensajes(controller.signal)
      .then(({ data }) => {
        const pago = data?.data?.datos_pago;
        if (!pago) return;
        Object.assign(DATOS_PAGO, {
          banco: pago.banco,
          cuenta: pago.cuenta,
          clabe: pago.clabe,
          rfc: pago.rfc,
          contacto: pago.telefono_contacto || null,
          correoCobranza: pago.correo_cobranza,
        });
      })
      .catch(() => {
        // Fallback silencioso: el módulo conserva sus valores locales.
      });

    return () => controller.abort();
  }, []);

  const rawTab = params.get('tab');
  const tab = TAB_KEYS.includes(rawTab) ? rawTab : 'tablero';
  const detalleId = params.get('credito');      // string | null
  const edoSel = params.get('edo') || null;
  const convSel = params.get('conv') || null;

  // Muta una copia de los params actuales y navega. opts.replace evita llenar
  // el historial (cambios de tab); sin replace = push (para que "atrás" cierre).
  const patch = (mut, opts) => {
    const p = new URLSearchParams(params);
    mut(p);
    setParams(p, opts);
  };

  const setTab = (key) => patch((p) => {
    p.set('tab', key);
    p.delete('credito');
    if (key !== 'edos') p.delete('edo');
    if (key !== 'convers') p.delete('conv');
  }, { replace: true });

  const openDetalle = (id) => patch((p) => { p.set('credito', String(id)); }); // push → "atrás" regresa a la lista
  const closeDetalle = () => patch((p) => { p.delete('credito'); }, { replace: true });

  const setEdoSel = (id) => patch((p) => {
    p.set('tab', 'edos');
    p.delete('conv');
    if (id) p.set('edo', String(id)); else p.delete('edo');
  }, { replace: true });

  const goEdos = (id) => patch((p) => {
    p.set('tab', 'edos');
    p.delete('credito');
    p.delete('conv');
    if (id) p.set('edo', String(id)); else p.delete('edo');
  });

  const setConvSel = (id) => patch((p) => {
    p.set('tab', 'convers');
    p.delete('edo');
    if (id) p.set('conv', String(id)); else p.delete('conv');
  }, { replace: true });

  /*
   * Ir a Conversaciones CON un hilo abierto, desde otra pestaña (el badge de
   * mensajes sin leer de la matriz). Gemelo de goEdos, no de setConvSel: ese
   * último se le pasa solo a Conversaciones para que sincronice su propia
   * selección.
   *
   * 🔴 `p.delete('credito')` es obligatorio, no higiene: si el param `credito`
   * sobrevive, el early-return de abajo pinta el detalle del crédito y
   * Conversaciones no se ve nunca. Es el fallo más confuso posible — el clic
   * "llevaría" a otro lado sin ningún error.
   *
   * La key del hilo la decide el backend: el conversacion_id cuando ya existe
   * conversación, o 'nuevo-{credito_id}' cuando el contrato aún no tiene hilo.
   * Adivinarla al revés deja el panel derecho vacío y en silencio.
   */
  const goConvers = (key) => patch((p) => {
    p.set('tab', 'convers');
    p.delete('credito');
    p.delete('edo');
    if (key) p.set('conv', String(key)); else p.delete('conv');
  });

  // Detalle de un crédito (se abre desde cualquier tab).
  if (detalleId) {
    return (
      <div className="cartera-dash">
        <CarteraDetalle id={detalleId} onBack={closeDetalle} />
      </div>
    );
  }

  return (
    <div className="cartera-dash">
      <div className="cart-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'tablero' && (
        <CarteraDashboard onGoClientes={() => setTab('clientes')} onOpenCredito={openDetalle} />
      )}
      {tab === 'clientes' && <CarteraLista onOpen={openDetalle} />}
      {tab === 'matriz' && (
        <CarteraMatriz onOpenCredito={openDetalle} onGoConversacion={goConvers} />
      )}
      {tab === 'cobranza' && (
        <CarteraCobranzaMes onOpenCredito={openDetalle} onOpenEdo={goEdos} />
      )}
      {tab === 'edos' && (
        <CarteraEstadosCuenta preselectId={edoSel} onSelectContrato={setEdoSel} onOpenCredito={openDetalle} />
      )}
      {tab === 'convers' && (
        <CarteraConversaciones
          preselectId={convSel}
          onSelectConversacion={setConvSel}
          onOpenCredito={openDetalle}
          onOpenEdo={goEdos}
        />
      )}
      {tab === 'avisos' && <CarteraAvisos onGoConversaciones={() => setTab('convers')} />}
    </div>
  );
};

export default CarteraPage;
